import Vue from "vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import routes from "@/routes/routes";
import { BootstrapVue, BootstrapVueIcons } from "bootstrap-vue";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
import "@/styles/custom.scss";
import VueRouter from "vue-router";
import App from "./App.vue";
import DashboardPlugin from "./dashboard-plugin";
import Notifications from "@/components/NotificationPlugin";
import { AxiosStatic, default as axios } from "axios";
import moment from "moment";
import vueNumeralFilterInstaller from "vue-numeral-filter";
import * as signalR from "@microsoft/signalr";
import { User } from "./models/User";
// import * as dotenv from 'dotenv'; // Not needed in Vite
import VueHtmlToPaper from 'vue-html-to-paper';
import VueMask from "v-mask";
import Button from "@/components/Button.vue";
import Card from "@/components/Cards/Card.vue";
import { DashboardStatus } from "./models/DashboardStatus";
import { PhoneStats } from "./models/PhoneStats";
import LoadScript from "vue-plugin-load-script";
import MicrosoftAuth from "@/util/microsoftAuth";
import { dashboardSetup } from "./util/dashboardSetup";
import { BrowserAuthError } from "@azure/msal-browser";
import { setupCognito, getToken as getCognitoToken } from "@/util/cognitoAuth";
import { Permission } from "@/models/Permission";
import VueShowdown from "vue-showdown";
import { AuthProviderFactory } from "@/util/authProviderFactory";
import { formatPhoneFilter, formatPhoneTwilioFilter } from "@/util/PhoneFormatter";
import { AuthProviderType } from "@/util/authProviderInterface";
import userInfoService, { UserInfoError, UserInfoServiceError } from "@/util/userInfoService";
import 'element-ui/lib/theme-chalk/index.css';

// Extend Vue prototype
declare module 'vue/types/vue' {
    interface Vue {
        $authProviderType: AuthProviderType;
    }
}

// Configuration interface
interface AppConfig {
    VITE_APP_USE_COGNITO: string;
    VITE_APP_AUTH_PROVIDER?: string;
    VITE_APP_ENVIRONMENT_NAME: string;
    VITE_STRIPE_PUBLIC_KEY: string;
    VITE_APP_APIURL: string;
    VITE_APP_ADAL_GRAPHID: string;
    VITE_APP_ADAL_TENANT: string;
    VITE_APP_CENTRAL_FILL_ENABLED: string;
    VITE_APP_OIDC_AUTHORITY?: string;
    VITE_APP_OIDC_CLIENT_ID?: string;
    VITE_APP_OIDC_SCOPES?: string;
    VITE_APP_OIDC_KNOWN_AUTHORITIES?: string;
    VITE_APP_OIDC_PROMPT?: string;
    VITE_APP_CLIENT_INTEGRATIONS_URL?: string;
    VITE_APP_CLIENT_INTEGRATIONS_API_KEY?: string;
}

// Load configuration asynchronously
async function loadConfig(): Promise<AppConfig> {
    try {
        const response = await fetch('/appConfig.json');
        if (!response.ok) {
            throw new Error(`Failed to load config: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Failed to load appConfig.json:', error);
        // Fallback to empty config or defaults
        return {
            VITE_APP_USE_COGNITO: 'false',
            VITE_APP_ENVIRONMENT_NAME: '',
            VITE_STRIPE_PUBLIC_KEY: '',
            VITE_APP_APIURL: '',
            VITE_APP_ADAL_GRAPHID: '',
            VITE_APP_ADAL_TENANT: '',
            VITE_APP_CENTRAL_FILL_ENABLED: 'false'
        };
    }
}

// Declaring global variables
declare module "vue/types/vue" {
    interface Vue {
        $http: AxiosStatic;
        $user: User;
        $stripeKey: string;
        $permissions: Permission[];
        $pageNames: Record<string, string>;
        $config: Record<string, string>;
    }
}
// Initialize the application
async function initializeApp() {
    const config = await loadConfig();

    // Add config to Vue prototype
    Vue.prototype.$config = config;

    const useCognito = config.VITE_APP_USE_COGNITO === "true";
    console.log("****UseCognito?:", config.VITE_APP_USE_COGNITO, useCognito);


    Vue.prototype.$environment = config.VITE_APP_ENVIRONMENT_NAME ?? "";
    Vue.prototype.$version = "";

    const serverURL = config.VITE_APP_APIURL ?? '';

    axios.defaults.baseURL = `${serverURL}/api`;
    axios.defaults.headers.post["Content-Type"] = "application/json";
    axios.defaults.withCredentials = true;
    const router = new VueRouter({
        routes,
        mode: "history",
        linkActiveClass: "active",
        scrollBehavior(to: any, _: any, savedPosition: any) {
            if (to?.meta?.preserveScroll || to?.query?.preserveScroll) return {};
            if (savedPosition) return savedPosition;
            return { x: 0, y: 0 };
        }
    } as Record<string, any>);

    router.beforeEach(async (to, _, next) => {
        if (to.meta?.permission || to.meta?.anyPermission) {
            const intendedPath = encodeURIComponent(to.fullPath);

            try {
                if (!useCognito) {
                    await MicrosoftAuth.ensureAuthenticated();

                    if (!MicrosoftAuth.isTokenValid()) {
                        await MicrosoftAuth.fetchToken();
                    }
                }

                // Use unified user info service
                await userInfoService.fetchUserInfo();
            } catch (error) {
                if (error instanceof UserInfoServiceError) {
                    switch (error.type) {
                        case UserInfoError.AUTH_ERROR:
                            next(`/unauthorized?authError=1&redirect=${intendedPath}`);
                            break;
                        case UserInfoError.NETWORK_ERROR:
                            next(`/unauthorized?networkError=1&redirect=${intendedPath}`);
                            break;
                        default:
                            console.error("Route guard error:", error);
                            next(`/unauthorized?networkError=1&redirect=${intendedPath}`);
                            break;
                    }
                } else {
                    next(`/unauthorized?networkError=1&redirect=${intendedPath}`);
                }
                return;
            }

            if (to.meta?.permission) {
                const hasPermission = Vue.prototype.$permissions.some((p: Permission) => p.permissionCodeName.toLowerCase() === to.meta?.permission?.toLowerCase());
                if (!hasPermission) {
                    next(`/unauthorized?permission=${to.meta?.permission}&redirect=${intendedPath}`);
                    return;
                }
            } else if (to.meta?.anyPermission) {
                const hasAnyPermission = Vue.prototype.$permissions.some((p: Permission) => to.meta?.anyPermission.some((ap: string) => ap.toLowerCase() === p.permissionCodeName.toLowerCase()));
                if (!hasAnyPermission) {
                    next(`/unauthorized?permission=${to.meta?.anyPermission.join(" or ")}&redirect=${intendedPath}`);
                    return;
                }
            }
        }

        if (to.path.startsWith("/cphub")) {
            next(to.path.replace("/cphub", "/processing"));
        } else {
            next();
        }
    });

    // Initialize authentication using factory pattern
    const authProviderType = AuthProviderFactory.getAuthProviderType(config);
    const authProvider = AuthProviderFactory.createProvider(authProviderType);

    // Make authProviderType available globally
    Vue.prototype.$authProviderType = authProviderType;

    if (authProviderType === "Cognito") {
        authProvider.install(Vue, { serverUrl: serverURL });
    } else {
        // MSAL or OIDC
        const msalConfig = AuthProviderFactory.createMsalConfig(authProviderType, config);
        authProvider.install(Vue, {
            serverUrl: serverURL,
            msalConfig: msalConfig,
            authProviderType: authProviderType
        });
    }

    Vue.config.productionTip = true;
    Vue.prototype.$http = axios;

    Vue.use(BootstrapVue, { BTable: { sortIconLeft: true, noSortReset: true } });
    Vue.use(BootstrapVueIcons);
    Vue.use(VueRouter);
    Vue.use(DashboardPlugin);
    Vue.use(Notifications);
    Vue.use(vueNumeralFilterInstaller, { locale: "en" });
    Vue.use(VueMask);

    // @ts-ignore
    Vue.use(LoadScript);

    const htmlToPaperOptions = {
        name: "_blank",
        specs: ["fullscreen=yes", "titlebar=yes", "scrollbars=yes"],
        styles: [
            "https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css",
            "https://unpkg.com/kidlat-css/css/kidlat.css",
        ],
        timeout: 1000, // default timeout before the print window appears
        autoClose: false, // if false, the window will not close after printing
        windowTitle: window.document.title, // override the window title
    };
    Vue.use(VueHtmlToPaper, htmlToPaperOptions);

    Vue.use(VueShowdown);

    Vue.component("VSelect", vSelect);

    Vue.component(Button.name, Button);
    Vue.component(Card.name, Card);

    const store = Vue.observable({
        queueItems: [],
        autoModeEnabled: false,
        currentAutoModeQueue: null,
        autoModeSessionId: null,
        lastAutoModeResponse: null
    });
    Vue.prototype.$store = store;

    // Custom filters, this should be better to be declared in a separate folder if we use more of this custom filters
    Vue.filter("formatDate", (value: Date, format: string, setLocal = true) => {
        if (value && format) {
            let momentDate = moment.utc(value);
            momentDate = setLocal ? momentDate.local() : momentDate;
            return momentDate.format(format);
        } else return "";
    });

    Vue.filter("formatLocalDate", (value: Date, format: string, _setLocal = true) => {
        if (value && format) {
            const momentDate = moment(value);
            return momentDate.format(format);
        } else return "";
    });

    // Phone number formatting filters
    Vue.filter("formatPhone", formatPhoneFilter);
    Vue.filter("formatPhoneTwilio", formatPhoneTwilioFilter);

    const updateTime: Date | null = null;

    const app = new Vue({
        data: {
            dispenseErrorCount: 0,
            taskCount: 0,
            notificationCount: 0,
            dashboardData: new DashboardStatus(),
            dashboardDataUpdated: updateTime,
            phoneStats: new PhoneStats(),
            environment: "",
            version: "",
            dataHubFileVersion: {},
            dataHubPhoneStats: {
                longestHold: null,
                callsHolding: null,
                repsReady: null
            },
            dataHubState: "",
            user: new User(),
            permissions: [],
            pageNames: {},
            userSettings: {},
        },
        async created() {
            if (this.$authProviderType === "Cognito") {
                console.log("startedBeforeCreate");
                const connectionsOptions = {
                    accessTokenFactory: async () => {
                        const resp = await getCognitoToken();
                        return resp;
                    },
                } as signalR.IHttpConnectionOptions;
                dashboardSetup(this, serverURL, connectionsOptions);
            } else {
                await this.$msal.instance.handleRedirectPromise();

                const isHomeRoute = this.$route?.name === "Home";
                let isAuthenticated = this.$msal.isAuthenticated();

                if (!isAuthenticated && !isHomeRoute) {
                    try {
                        await MicrosoftAuth.ensureAuthenticated();
                        isAuthenticated = this.$msal.isAuthenticated();
                    } catch (e) {
                        if (e instanceof BrowserAuthError) {
                            await this.$router.push({ name: "Home" });
                        }
                    }
                }

                if (!isAuthenticated) return;

                const connectionsOptions = {
                    accessTokenFactory: async () => {
                        const token = await MicrosoftAuth.fetchToken();
                        return token.accessToken;
                    },
                } as signalR.IHttpConnectionOptions;
                dashboardSetup(this, serverURL, connectionsOptions);

                if (isHomeRoute) await this.$router.push("/processing");
            }
        },
        render: h => h(App),
        router,
    });

    Object.defineProperty(Vue.prototype, '$user', {
        get() {
            return app.user;
        },
        set(value) {
            app.user = value ?? new User();
        },
    });

    Object.defineProperty(Vue.prototype, '$permissions', {
        get() {
            return app.permissions;
        },
        set(value) {
            app.permissions = value ?? [];
        },
    });

    Object.defineProperty(Vue.prototype, '$pageNames', {
        get() {
            return app.pageNames;
        },
        set(value) {
            app.pageNames = value ?? {};
        },
    });

    app.$mount("#app");
}

// Start the application
initializeApp().catch(error => {
    console.error('Failed to initialize application:', error);
});
